import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import RankingSidebar from '@/components/feature/RankingSidebar';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, rankings, latestUpdates, recommended } from '@/mocks/comics';

export default function Home() {
  const [activeTab, setActiveTab] = useState<'weekly' | 'monthly'>('weekly');
  const currentRankings = rankings[activeTab];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="relative h-[520px] md:h-[620px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20manga%20style%20background%20with%20abstract%20comic%20panel%20lines%20and%20speed%20effects%2C%20warm%20golden%20and%20deep%20red%20accents%2C%20dramatic%20composition%2C%20cinematic%20atmosphere%2C%20no%20text%2C%20artistic%20digital%20illustration%2C%20clean%20minimal%20abstract%20design%20with%20brush%20stroke%20textures&width=1600&height=900&seq=hero-bg-manga&orientation=landscape"
              alt="土豪漫画网 - 免费漫画在线阅读"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="max-w-2xl animate-[fadeInUp_0.6s_ease]">
                <h1 className="font-heading text-3xl md:text-5xl text-foreground-50 leading-tight">
                  发现最精彩的
                  <span className="text-primary-500">韩国漫画</span>
                </h1>
                <p className="mt-4 text-foreground-200 text-base md:text-lg leading-relaxed max-w-lg">
                  土豪漫画网提供<strong>免费漫画下拉式</strong>浏览和<strong>高清韩漫人气榜</strong>，在线观看韩国漫画，追踪热门趋势，享受视觉盛宴。
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Link
                    to="/category/hanman"
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    立即浏览韩漫
                  </Link>
                  <Link
                    to="/category/guoman"
                    className="px-6 py-3 bg-background-50/20 hover:bg-background-50/30 text-foreground-50 border border-foreground-50/30 font-medium rounded-lg transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    探索国漫专区
                  </Link>
                </div>
                <div className="flex items-center gap-6 mt-8">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-accent-500/20 rounded-lg">
                      <i className="ri-fire-line text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-foreground-50 text-sm font-medium">10000+</p>
                      <p className="text-foreground-400 text-xs">漫画资源</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-accent-500/20 rounded-lg">
                      <i className="ri-user-line text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-foreground-50 text-sm font-medium">1000万+</p>
                      <p className="text-foreground-400 text-xs">注册用户</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-accent-500/20 rounded-lg">
                      <i className="ri-download-line text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-foreground-50 text-sm font-medium">每日更新</p>
                      <p className="text-foreground-400 text-xs">百部连载</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">漫画分类</h2>
              <p className="text-foreground-500 mt-2 text-sm">探索你感兴趣的漫画类型，发现更多精彩内容</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-background-100 rounded-lg group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className={`${cat.icon} text-lg text-foreground-600 group-hover:text-primary-600 transition-colors`}></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-foreground-950">{cat.name}</h3>
                    <p className="text-xs text-foreground-400 truncate">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 bg-background-100/50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">人气排行榜</h2>
                <p className="text-foreground-500 mt-1 text-sm">基于真实阅读数据，发现最热门漫画</p>
              </div>
              <div className="flex bg-background-200 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('weekly')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'weekly'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  周榜
                </button>
                <button
                  onClick={() => setActiveTab('monthly')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'monthly'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  月榜
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {currentRankings.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  cover={comic.cover}
                  category={comic.category}
                  rating={comic.rating}
                  status={comic.status}
                  rank={comic.rank}
                  variant="ranking"
                />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/hanman"
                className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors"
              >
                查看更多排行 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">最新更新</h2>
                    <p className="text-foreground-500 mt-1 text-sm">最新章节实时更新，追更不等待</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {latestUpdates.map((comic) => (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      cover={comic.cover}
                      category={comic.category}
                      newChapter={comic.newChapter}
                      updateTime={comic.updateTime}
                    />
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-[300px] flex-shrink-0">
                <RankingSidebar rankings={[...rankings.weekly, ...rankings.monthly].filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i)} />
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 bg-background-100/50">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">编辑推荐</h2>
              <p className="text-foreground-500 mt-2 text-sm">精选优质漫画，为你发现下一部必看佳作</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {recommended.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  cover={comic.cover}
                  category={comic.category}
                  rating={comic.rating}
                  status={comic.status}
                />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/hanman"
                className="px-6 py-2.5 bg-background-50 border border-background-300 text-foreground-700 hover:text-foreground-950 hover:border-primary-300 font-medium rounded-lg transition-all text-sm whitespace-nowrap cursor-pointer"
              >
                浏览全部漫画
              </Link>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-14">
          <div className="max-w-[1280px] mx-auto">
            <div className="bg-primary-500 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-background-50 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-background-50 rounded-full translate-y-1/2 -translate-x-1/2"></div>
              </div>
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl text-background-50">开始你的漫画之旅</h2>
                <p className="mt-3 text-background-50/80 text-base max-w-lg mx-auto">
                  海量高清漫画免费看，下拉式阅读更流畅。加入千万漫画爱好者的社区，发现属于你的精彩世界。
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                  <Link
                    to="/category/hanman"
                    className="px-6 py-3 bg-background-50 text-primary-600 font-medium rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始免费阅读
                  </Link>
                  <Link
                    to="/about"
                    className="px-6 py-3 bg-primary-600 text-background-50 font-medium rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}